import { supabase } from '../supabase';
import {
  useQuery,
  useMutation,
  useQueryClient
} from '@tanstack/react-query';
import { Notification as NotificationType, NotificationGroup, UserNotification } from '@shiftly/shared/src/schema';
import { DateTime } from 'luxon';


interface SupabaseNotification {
  id: string;
  title: string;
  message: string;
  type: string;
  group: string;
  created_at: string;
  created_by: string;
  target_users: string[] | null;
  link: string | null;
  source: string | null;
  source_user_id: string | null;
}

interface SupabaseUserNotification {
  id: string;
  notification_id: string;
  user_id: string;
  read: boolean;
  read_at: string | null;
  created_at: string;
  notifications?: SupabaseNotification;
}

const SupabaseNotificationService = {
  async getUserNotifications(user_id: string) {
    try {
      // Calculate timestamp for 12 hours ago (matching Firebase implementation)
      const twelveHoursAgo = DateTime.utc().minus({ hours: 12 });

      // Build query for user notifications with notification details
      const { data: rawUserNotifications, error } = await supabase
        .from('user_notifications')
        .select(`
          *,
          notifications (*)
        `)
        .eq('user_id', user_id)
        .or(`read.eq.false,and(read.eq.true,read_at.gte.${twelveHoursAgo.toISO()})`)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Transform the data to match the expected interface
      const userNotifications: (UserNotification & { notification: NotificationType })[] = (rawUserNotifications || [])
        .filter((un: SupabaseUserNotification) => un.notifications)
        .map((un: SupabaseUserNotification) => ({
          id: un.id,
          notification_id: un.notification_id,
          user_id: un.user_id,
          read: un.read,
          read_at: un.read_at ? DateTime.fromISO(un.read_at, { zone: 'UTC' }) : undefined,
          created_at: DateTime.fromISO(un.created_at, { zone: 'UTC' }),
          notification: {
            id: un.notifications!.id,
            title: un.notifications!.title,
            message: un.notifications!.message,
            type: un.notifications!.type as NotificationType['type'],
            group: un.notifications!.group as NotificationGroup,
            created_at: DateTime.fromISO(un.notifications!.created_at, { zone: 'UTC' }),
            created_by: un.notifications!.created_by,
            target_users: un.notifications!.target_users || undefined,
            link: un.notifications!.link || undefined,
            source: un.notifications!.source || undefined,
            source_user_id: un.notifications!.source_user_id || undefined,
          }
        }));

      return userNotifications;
    } catch (error) {
      console.error('Error in getUserNotifications:', error);
      return [];
    }
  },

  async deleteUserNotification(userNotificationId: string) {
    try {
      const { error } = await supabase
        .from('user_notifications')
        .delete()
        .eq('id', userNotificationId);

      if (error) throw error;
    } catch (error) {
      console.error('Error in deleteUserNotification:', error);
    }
  },

  async getUnreadCount(user_id: string) {
    try {
      const { count, error } = await supabase
        .from('user_notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user_id)
        .eq('read', false);

      if (error) throw error;

      return count || 0;
    } catch (error) {
      console.error('Error in getUnreadCount:', error);
      return 0;
    }
  },

  async markAsRead(userNotificationId: string) {
    try {
      const { error } = await supabase
        .from('user_notifications')
        .update({
          read: true,
          read_at: DateTime.utc().toISO()
        })
        .eq('id', userNotificationId);

      if (error) throw error;
    } catch (error) {
      console.error('Error in markAsRead:', error);
    }
  },

  async markAllAsRead(user_id: string) {
    try {
      const { error } = await supabase
        .from('user_notifications')
        .update({
          read: true,
          read_at: DateTime.utc().toISO()
        })
        .eq('user_id', user_id)
        .eq('read', false);

      if (error) throw error;
    } catch (error) {
      console.error('Error in markAllAsRead:', error);
    }
  },

  async createNotification(notification: Omit<NotificationType, 'id' | 'created_at'>) {
    try {
      // Create the notification
      const { data: notificationData, error: notificationError } = await supabase
        .from('notifications')
        .insert({
          title: notification.title,
          message: notification.message,
          type: notification.type,
          group: notification.group,
          created_by: notification.created_by === 'system' ? null : notification.created_by,
          target_users: notification.target_users || null,
          link: notification.link || null,
          source: notification.source || null,
          source_user_id: notification.source_user_id || null
        })
        .select()
        .single();

      if (notificationError) throw notificationError;

      const notificationId = notificationData.id;

      // Create user notifications if targetUsers is specified
      if (notification.target_users && notification.target_users.length > 0) {
        const userNotifications = notification.target_users.map(user_id => ({
          notification_id: notificationId,
          user_id: user_id,
          read: false
        }));

        const { error: userNotificationError } = await supabase
          .from('user_notifications')
          .insert(userNotifications);

        if (userNotificationError) throw userNotificationError;
      }

      return notificationId;
    } catch (error) {
      console.error('Error in createNotification:', error);
    }
  },
};

// React Query Hooks
export const useUserNotifications = (user_id: string) => {
  return useQuery({
    queryKey: ['notifications', user_id],
    queryFn: () => SupabaseNotificationService.getUserNotifications(user_id),
    enabled: !!user_id,
    staleTime: 30000,
    refetchOnWindowFocus: false
  });
};

export const useDeleteUserNotification = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (params: { id: string, notification?: { title: string } }) => {
      try {        
        return SupabaseNotificationService.deleteUserNotification(params.id);
      } catch (error) {
        console.error('Error in deleteUserNotification mutation:', error);
        return SupabaseNotificationService.deleteUserNotification(params.id);
      }
    },
    onSuccess: (_, __) => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    }
  });
};

export const useUnreadCount = (user_id: string) => {
  return useQuery({
    queryKey: ['notifications', user_id, 'unread'],
    queryFn: () => SupabaseNotificationService.getUnreadCount(user_id),
    enabled: !!user_id,
    staleTime: 30000,
    refetchOnWindowFocus: false
  });
};

export const useMarkAsRead = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (params: { id: string, notification?: { title: string } }) => {
      try {        
        return SupabaseNotificationService.markAsRead(params.id);
      } catch (error) {
        console.error('Error in markAsRead mutation:', error);
        return SupabaseNotificationService.markAsRead(params.id);
      }
    },
    onMutate: async (params) => {
      await queryClient.cancelQueries({ queryKey: ['notifications'] });

      const previousNotifications = queryClient.getQueryData(['notifications']);

      queryClient.setQueryData(['notifications'], (old: any) => {
        if (!old) return old;
        return old.map((notification: any) => {
          if (notification.id === params.id) {
            return {
              ...notification,
              read: true,
              readAt: DateTime.utc()
            };
          }
          return notification;
        });
      });

      return { previousNotifications };
    },
    onError: (_, __, context) => {
      if (context?.previousNotifications) {
        queryClient.setQueryData(['notifications'], context.previousNotifications);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    }
  });
};

export const useMarkAllAsRead = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (user_id: string) => {
      return SupabaseNotificationService.markAllAsRead(user_id);
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['notifications'] });

      const previousNotifications = queryClient.getQueryData(['notifications']);

      queryClient.setQueryData(['notifications'], (old: any) => {
        if (!old) return old;
        return old.map((notification: any) => ({
          ...notification,
          read: true,
          readAt: DateTime.utc()
        }));
      });

      return { previousNotifications };
    },
    onError: (_, __, context) => {
      if (context?.previousNotifications) {
        queryClient.setQueryData(['notifications'], context.previousNotifications);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    }
  });
};

export const useCreateNotification = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (notification: Omit<NotificationType, 'id' | 'created_at'>) => 
      SupabaseNotificationService.createNotification(notification),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    }
  });
};


export default SupabaseNotificationService;